// @ts-ignore
import React from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { LanguageSelector } from "@/components/global-footer/language-selector/src";
import { LinkWithIcon } from "@/components/global-nav/list-of-links/src";
import { useLocalizedHref } from "@/libraries/web-framework";

import Link from "../../../components/wpl/link/src/index";

// ==================================================================
// CSS styling
// ==================================================================
const containerStyle = css({
    width: "100%",
    maxWidth: "1216px",
    margin: "auto",
    paddingBlock: "24px",
    paddingInline: token("space.200"),
    display: "flex",
    flexDirection: "column",
    gap: token("space.400"),

    [media.above.sm]: {
        paddingInline: "44px",
        gap: token("space.400")
    },

    [media.above.md]: {
        paddingInline: "24px",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center"
    }
});

const linkStyle = css({
    "fontSize": "14px",
    "lineHeight": "14px",
    "height": "14px",
    "textDecoration": "none",

    ":hover": {
        textDecoration: "underline",
        textShadow: "none"
    },

    ":visited, :focus": {
        textShadow: "none"
    }
});

const copyrightStyle = css({
    color: "#505258",
    lineHeight: "18px",
    height: "18px",
    fontSize: "14px",

    [media.above.md]: {
        textAlign: "center"
    }
});

const linksAndLanguageStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.200"),

    [media.above.sm]: {
        flexDirection: "row",
        gap: token("space.400"),
        alignItems: "center"
    }
});

// ==================================================================
// Type Props
// ==================================================================
export interface GlobalFooterBottomProps {
    selectedLanguage?: string;
    onLanguageChangeHandler?: React.Dispatch<string>;
    className?: string;
}

export interface GlobalFooterBottomFallbackProps {
    className?: string;
}

// ==================================================================
// Rendering Functions
// ==================================================================
const BottomLinks = () => {
    const intl = useIntl();
    const bottomLinks = [
        {
            text: intl.formatMessage({
                id: "globalfooterv2.bottom.privacy-policy",
                defaultMessage: "Privacy policy"
            }),
            href: useLocalizedHref("/legal/privacy-policy"),
            isOneTrustWrapped: false
        },
        {
            text: intl.formatMessage({
                id: "globalfooterv2.bottom.terms",
                defaultMessage: "Terms"
            }),
            href: useLocalizedHref("/legal/cloud-terms-of-service"),
            isOneTrustWrapped: false
        },
        {
            text: intl.formatMessage({
                id: "globalfooterv2.bottom.impressum",
                defaultMessage: "Impressum"
            }),
            href: useLocalizedHref("/legal/impressum"),
            isOneTrustWrapped: false
        }
    ];

    return bottomLinks.map((bottomLink) => {
        if (bottomLink.isOneTrustWrapped) {
            return (
                <div
                    key={bottomLink.text}
                    className={"link-wrapper atl-policy-link-container"}
                >
                    <LinkWithIcon
                        linkText={bottomLink.text}
                        href={bottomLink.href}
                        type="anchor"
                        isHaveIcon={false}
                        isUsePlainSpanTagForLinkText={true}
                        className={"atl-policy-link"}
                        spanLinkTextClassName={"atl-policy-link-text"}
                    />
                </div>
            );
        } else {
            return (
                <Link
                    key={bottomLink.text}
                    text={bottomLink.text}
                    href={bottomLink.href}
                    type="anchor"
                    customLinkColor={"#101214"}
                    css={linkStyle}
                />
            );
        }
    });
};

const copyRightYear = new Date().getFullYear();

export const GlobalFooterBottomFallback = ({
    className
}: GlobalFooterBottomFallbackProps) => {
    return (
        <div className={className} css={containerStyle}>
            <div css={copyrightStyle}>
                Copyright © {copyRightYear} Atlassian
            </div>
            <div css={linksAndLanguageStyle}>{BottomLinks()}</div>
        </div>
    );
};
export const GlobalFooterBottom = ({
    selectedLanguage,
    onLanguageChangeHandler,
    className
}: GlobalFooterBottomProps) => {
    return (
        <div className={className} css={containerStyle}>
            <div css={copyrightStyle}>
                Copyright © {copyRightYear} Atlassian
            </div>
            <div css={linksAndLanguageStyle}>
                {BottomLinks()}
                <LanguageSelector
                    selectedLanguage={selectedLanguage}
                    onLanguageChangeHandler={onLanguageChangeHandler}
                />
            </div>
        </div>
    );
};
